import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../context/ViewerOrderContext';
import { useReporting } from '../context/ReportingContext';
import Loader from './Loader/Loader';
import CustomSnackbar from './CustomSnackbar';
import Tooltip from '@mui/material/Tooltip';
import { mockOpenOrders } from '../data/mockOrders';
import { mockViewerData } from '../data/mockViewerData';
import { Button } from '@mui/material';
import { Typography } from '@mui/material';

const TableContainer = styled.div`
  min-height: 60vh;
  margin: 0 36px 20px 0;
  border-radius: 12px;
  border-style: none;
  background: #fff;
  padding: 0px 36px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  ${'' /* padding: 20px 20px; */}
  background: #fff;
  border-radius: 12px;
  margin-top: 20px;
  width: 100%;
`;

const Table = styled.div`
  width: 100%;
  border-collapse: collapse;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 20px;
  border-bottom: 1px solid #e9ecef;
`;

const TableHeaderRow = styled(TableRow)`
  background: #E9E7FD;
  border-radius: 12px;
`;

const TableCell = styled.div`
  text-align: left;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 19.5px;
  text-transform: uppercase;
  color: #667085;
  
  &:nth-child(1) { width: 17%; }
  &:nth-child(2) { width: 15%; }
  &:nth-child(3) { width: 8%; }
  &:nth-child(4) { width: 8%; }
  &:nth-child(5) { width: 10%; }
  &:nth-child(6) { width: 15%; }
  &:nth-child(7) { width: 18%; }
  &:nth-child(8) { width: 15%; }
`;

const EmergencyMarker = styled.span`
  color: red;
  font-weight: bold;
  margin-right: 5px;
  cursor: pointer;
`;

const TableDataCell = styled(TableCell)`
  font-weight: 400;
  font-size: 13px;
  color: #000;
  text-transform: none;
`;

const OpenLink = styled.a`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #4862F2;
  cursor: pointer;
  text-align: center;
`;

const NoDataWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const NoDataMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  text-align: center;
  padding: 40px;
  ${'' /* background: #f8f9fa; */}
  border-radius: 8px;
  margin: 20px 0;
`;

const NoDataTitle = styled.h3`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`;

const HighlightedText = styled.span`
  color: #4862F2;
`;

const NoDataDescription = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 14px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
`;

const Tab = styled.div`
  padding: 10px 20px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#4862F2' : '#667085'};
  border-bottom: 2px solid ${props => props.active ? '#4862F2' : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    color: #4862F2;
  }
`;

const BoldModality = styled(TableDataCell)`
  font-weight: 600;
  color: #000;
  font-size: 14px;
`;

const StartReportButton = styled(Button)`
  color: ${props => props.modality === 'X-Ray' ? '#3a4bc1' : '#666'}!important;
  background: none;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 800!important;
  cursor: ${props => props.modality === 'X-Ray' ? 'pointer' : 'not-allowed'};
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.modality === 'X-Ray' ? '#3a4bc1' : '#666'};
    background: none;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FixedContainer = styled.div`
  position: fixed;
  top: 80%;
  right: 20%;
  width:40vw;
  background-color: white;
  color: black;
  padding: 20px;
  z-index: 1000;
  font-family: 'Poppins', sans-serif;
`;

const StyledLink = styled.a`
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const OpenOrderTable = () => {
  const navigate = useNavigate();
  const { setOrderData } = useOrder();
  const { isReporting } = useReporting();
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [messageSnackbar, setMessageSnackbar] = useState('');
  const [severitySnackbar, setSeveritySnackbar] = useState('success');

  const handleOpenClick = async (
    studyId,
    orderId,
    displayOrderId,
    patientName,
    age,
    sex,
    labUserId,
    meddream_id,
    username
  ) => {
    // if (!isReporting) {
    //   setOpenSnackbar(true);
    //   setMessageSnackbar('Please start reporting to view cases');
    //   setSeveritySnackbar('error');
    //   return;
    // }

    setLoading(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get mock data for the case using displayOrderId
      const viewerData = mockViewerData[displayOrderId];
      
      if (!viewerData) {
        throw new Error('No viewer data available for this case');
      }

      // Fetch token from API
      let viewerToken = '';
      try {
        const tokenResponse = await fetch('https://uatai.natoe.ai/api/generate_token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                study: studyId,
                storage: "PACS"
              }
            ],
            "user": {
              "id": meddream_id,
              "name": username
          }
          })
        });
        
        if (!tokenResponse.ok) {
          throw new Error('Failed to fetch token');
        }
        
        const tokenData = await tokenResponse.json();
        viewerToken = tokenData.token;
      } catch (tokenError) {
        console.error('Error fetching token:', tokenError);
        // Continue without token if there's an error
      }

      // Set the order data with mock viewer data
      setOrderData({
        data: viewerData.data,
        studyId: studyId,
        orderId: orderId,
        displayOrderId: displayOrderId,
        labUserId: labUserId,
        caseId: displayOrderId,
        viewerToken: viewerToken,
        patientName: patientName,
        age: age,
        sex: sex
      });
      
      navigate('/viewer');
    } catch (error) {
      console.error('Error in handleOpenClick:', error);
      setOpenSnackbar(true);
      setMessageSnackbar('Failed to open this order');
      setSeveritySnackbar('error');
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <FixedContainer>
        <StyledLink href="https://calendly.com/danita-yerkey-natoe/30min" target="_blank">
          <Typography variant="h6" style={{display: 'inline',fontWeight: 'bold', padding: '10px', color: '#4862F2'}}>
            Book A Demo Session With Us
          </Typography>
        </StyledLink>
      </FixedContainer>

      {loading && <Loader customText="Loading study..."/>}

      <TableContainer>
        <TableHeader>
          <TabContainer>
            <Tab active={true}>
              Open Cases
            </Tab>
          </TabContainer>
        </TableHeader>

        {mockOpenOrders.length === 0 ? (
          <NoDataWrapper>
            <NoDataMessage>
              <NoDataTitle>No Cases Available</NoDataTitle>
              <NoDataDescription>
                There are currently no open cases for review. Check back later for new cases.
              </NoDataDescription>
            </NoDataMessage>
          </NoDataWrapper>
        ) : (
          <Table>
            <TableHeaderRow>
              <TableCell>Patient Name</TableCell>
              <TableCell>Case ID</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Sex</TableCell>
              <TableCell>Modality</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Lab Name</TableCell>
              <TableCell>Action</TableCell>
            </TableHeaderRow>
            {mockOpenOrders.map((order, index) => (
              <TableRow key={index}>
                <TableDataCell>
                  {order.emergency && (
                    <Tooltip title="Marked as emergency" arrow>
                      <EmergencyMarker>🚨</EmergencyMarker>
                    </Tooltip>
                  )}
                  {order.patientName}
                </TableDataCell>
                <TableDataCell>#{order.displayOrderId}</TableDataCell>
                <TableDataCell>{order.age}</TableDataCell>
                <TableDataCell>{order.sex}</TableDataCell>
                <BoldModality>{order.modality}</BoldModality>
                <TableDataCell>{order.studyName}</TableDataCell>
                <TableDataCell>{order.labName}</TableDataCell>
                <TableDataCell>
                  <StartReportButton
                    modality={order.modality}
                    disabled={order.modality !== 'X-Ray'}
                    onClick={() =>
                      handleOpenClick(
                        order.studyId,
                        order.orderId,
                        order.displayOrderId,
                        order.patientName,
                        order.age,
                        order.sex,
                        order.labUserId,
                        order.meddream_id,
                        order.username
                      )
                    }
                  >
                    Start Reporting {'>'}
                  </StartReportButton>
                </TableDataCell>
              </TableRow>
            ))}
          </Table>
        )}
        <CustomSnackbar
          open={openSnackbar}
          message={messageSnackbar}
          severity={severitySnackbar}
          onClose={closeSnackbar}
        />
      </TableContainer>
    </>
  );
};

export default OpenOrderTable; 